import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// Slice de calendarios de festivos. Endpoint base: /api/holidayCalendar
// Lista mezcla oficiales + custom del restaurante. `IsPrimary` viene del
// backend según User.PrimaryHolidayCalendarId.

const initialState = {
  isLoading: false,
  error: null,
  calendars: [],
  // Cache de festivos por calendarId ({ [calendarId]: DtoHolidayGet[] }).
  // Se actualiza en getHolidays y en cada add/update/delete de holiday.
  holidaysByCalendar: {},
  lastImportResult: null,
};

function bumpHolidayCount(state, calendarId, delta) {
  const cal = state.calendars.find((c) => c.id === calendarId);
  if (cal) cal.holidayCount = Math.max(0, (cal.holidayCount || 0) + delta);
}

const slice = createSlice({
  name: "holidayCalendars",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    stopLoading(state) { state.isLoading = false; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },

    setCalendars(state, action) {
      state.calendars = action.payload || [];
      state.isLoading = false;
    },
    upsertCalendar(state, action) {
      const c = action.payload;
      const idx = state.calendars.findIndex((x) => x.id === c.id);
      if (idx >= 0) state.calendars[idx] = c;
      else state.calendars.push(c);
    },
    removeCalendar(state, action) {
      state.calendars = state.calendars.filter((c) => c.id !== action.payload);
      delete state.holidaysByCalendar[action.payload];
    },
    setPrimaryLocal(state, action) {
      state.calendars = state.calendars.map((c) => ({
        ...c,
        isPrimary: c.id === action.payload,
      }));
    },

    setHolidays(state, action) {
      const { calendarId, holidays } = action.payload;
      state.holidaysByCalendar[calendarId] = holidays || [];
    },
    upsertHoliday(state, action) {
      const { calendarId, holiday } = action.payload;
      const list = state.holidaysByCalendar[calendarId] || [];
      const idx = list.findIndex((h) => h.id === holiday.id);
      if (idx >= 0) list[idx] = holiday;
      else {
        list.push(holiday);
        // El contador de la lista de calendarios («· N festivos») viene del
        // servidor: sin esto seguía en 0 tras añadir hasta recargar.
        bumpHolidayCount(state, calendarId, +1);
      }
      state.holidaysByCalendar[calendarId] = [...list].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      );
    },
    removeHoliday(state, action) {
      const { calendarId, holidayId } = action.payload;
      const list = state.holidaysByCalendar[calendarId] || [];
      if (list.some((h) => h.id === holidayId)) bumpHolidayCount(state, calendarId, -1);
      state.holidaysByCalendar[calendarId] = list.filter((h) => h.id !== holidayId);
    },

    setLastImportResult(state, action) {
      state.lastImportResult = action.payload;
    },
  },
});

export default slice.reducer;
export const {
  setCalendars,
  upsertCalendar,
  removeCalendar,
  setPrimaryLocal,
  setHolidays,
  upsertHoliday,
  removeHoliday,
  setLastImportResult,
} = slice.actions;

// ── Thunks ──────────────────────────────────────────────────────────

export function getHolidayCalendars(includeInactive = false) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get(`/holidayCalendar?includeInactive=${includeInactive}`);
      dispatch(slice.actions.setCalendars(res.data || []));
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error cargando calendarios"));
    }
  };
}

export function createHolidayCalendar(payload) {
  return async (dispatch) => {
    const res = await axios.post("/holidayCalendar", payload);
    dispatch(slice.actions.upsertCalendar(res.data));
    return res.data;
  };
}

export function updateHolidayCalendar(payload) {
  return async (dispatch) => {
    const res = await axios.put("/holidayCalendar", payload);
    dispatch(slice.actions.upsertCalendar(res.data));
    return res.data;
  };
}

export function deleteHolidayCalendar(id) {
  return async (dispatch) => {
    await axios.delete(`/holidayCalendar/${id}`);
    dispatch(slice.actions.removeCalendar(id));
  };
}

export function setPrimaryHolidayCalendar(id) {
  return async (dispatch) => {
    await axios.post(`/holidayCalendar/${id}/setPrimary`);
    dispatch(slice.actions.setPrimaryLocal(id));
  };
}

export function getCalendarHolidays(calendarId, year) {
  return async (dispatch) => {
    const q = year ? `?year=${year}` : "";
    const res = await axios.get(`/holidayCalendar/${calendarId}/holidays${q}`);
    dispatch(slice.actions.setHolidays({ calendarId, holidays: res.data || [] }));
    return res.data;
  };
}

export function addHoliday(calendarId, payload) {
  return async (dispatch) => {
    const res = await axios.post(`/holidayCalendar/${calendarId}/holidays`, payload);
    dispatch(slice.actions.upsertHoliday({ calendarId, holiday: res.data }));
    return res.data;
  };
}

export function updateHoliday(calendarId, payload) {
  return async (dispatch) => {
    const res = await axios.put(`/holidayCalendar/${calendarId}/holidays`, payload);
    dispatch(slice.actions.upsertHoliday({ calendarId, holiday: res.data }));
    return res.data;
  };
}

export function deleteHoliday(calendarId, holidayId) {
  return async (dispatch) => {
    await axios.delete(`/holidayCalendar/${calendarId}/holidays/${holidayId}`);
    dispatch(slice.actions.removeHoliday({ calendarId, holidayId }));
  };
}

export function importHolidays(calendarId, request) {
  return async (dispatch) => {
    const res = await axios.post(`/holidayCalendar/${calendarId}/import`, request);
    dispatch(slice.actions.setLastImportResult(res.data));
    // Refresca los festivos del año importado para pintar la lista actualizada.
    if (request?.year) await dispatch(getCalendarHolidays(calendarId, request.year));
    return res.data;
  };
}
